import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Button, Icon } from '@nazka/nazka.mapframe.components';

import { transportLabels } from '@App/pages/Home/TransportSelect/transport-select.constants';
import { checkboxFilterLabels, daysSinceReleaseLimitOptions, languageLevels } from '@App/pages/Filter/filter.constants';
import { useVacancyFilterOptions } from '@services/query.service';

import './chips.styl';
import { faHouse, faRectanglePro } from '@fortawesome/pro-solid-svg-icons';
import { faClose, faCircleDashed, faClock } from '@fortawesome/pro-regular-svg-icons';

import {
  updateRefundBusTrain,
  updateOrganizationCollectiveTransport,
  updateOrganizationCarpool,
  updateCarSharingSubscription,
  updateElectricCarChargingStation,
  updateCompanyCar,
  updateTelecommutingIsPossible,
  updateFlexibleHours,
  updateBicycleAllowance,
  updateCompanyOrLeaseBikes,
  updateChargingStationEBikes,
  updateChangingRoomAndOrShowers,
  updateDaysSinceReleaseLimit,
  updateJob,
  updateJobDomain,
  updateWorkSchedule,
  updateTimeArrangement,
  updateContractType,
  updateEducationLevel,
  updateDegree,
  updateLanguages,
  updateDriverLicense,
} from '@App/pages/Filter/filter.slice';

function Chips() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const homeAddress = useSelector(state => state.map.homeAddress);
  const {
    searchType, distance, duration, transportType, daysSinceReleaseLimit, refundBusTrain,
    organizationCollectiveTransport, organizationCarpool, carSharingSubscription,
    electricCarChargingStation, companyCar, telecommutingIsPossible, flexibleHours,
    bicycleAllowance, companyOrLeaseBikes, chargingStationEBikes, changingRoomAndOrShowers,
    jobDomain, job, workSchedule, timeArrangement, contractType, educationLevel, degree, languages,
    driverLicense,
  } = useSelector(state => state.filter);

  const { data: filterOptions, isLoading } = useVacancyFilterOptions();

  const checkboxFilters = {
    refundBusTrain: { value: refundBusTrain, update: updateRefundBusTrain },
    organizationCollectiveTransport: { value: organizationCollectiveTransport, update: updateOrganizationCollectiveTransport },
    organizationCarpool: { value: organizationCarpool, update: updateOrganizationCarpool },
    carSharingSubscription: { value: carSharingSubscription, update: updateCarSharingSubscription },
    electricCarChargingStation: { value: electricCarChargingStation, update: updateElectricCarChargingStation },
    companyCar: { value: companyCar, update: updateCompanyCar },
    telecommutingIsPossible: { value: telecommutingIsPossible, update: updateTelecommutingIsPossible },
    flexibleHours: { value: flexibleHours, update: updateFlexibleHours },
    bicycleAllowance: { value: bicycleAllowance, update: updateBicycleAllowance },
    companyOrLeaseBikes: { value: companyOrLeaseBikes, update: updateCompanyOrLeaseBikes },
    chargingStationEBikes: { value: chargingStationEBikes, update: updateChargingStationEBikes },
    changingRoomAndOrShowers: { value: changingRoomAndOrShowers, update: updateChangingRoomAndOrShowers },
  };

  if (isLoading) return null;

  return (
    <ul className="chips">
      <li className="chips__chip chips__chip--home" onClick={() => navigate('/')}>
        <Icon className="chips__chip__icon" icon={faHouse} />
        {`${homeAddress?.place_name}`}
      </li>
      <li className="chips__chip chips__chip--type" onClick={() => navigate('/')}>
        <Icon className="chips__chip__icon" icon={searchType === 'duration' ? faClock : faCircleDashed} />
        {`${searchType === 'duration' ? `${duration} min` : `${distance} km`}`}
      </li>
      {searchType === 'duration' && (
        <li className={`chips__chip chips__chip--transport chips__chip--${transportType}`} onClick={() => navigate('/')}>
          <Icon className="chips__chip__icon" icon={`/img/icons/transport_types/${transportType}.svg`} />
          {`${transportLabels[transportType]}`}
          {transportType.includes('car') && (
            <Icon 
              className="chips__chip__pro"
              icon={faRectanglePro}
            />
          )}
        </li>
      )}
      {!!daysSinceReleaseLimit && (
        <li className="chips__chip">
          {`${daysSinceReleaseLimitOptions.find(o => o.id === daysSinceReleaseLimit).label} oud`}
          <Button 
            className="chips__chip__remove" 
            hover={false}
            layout="ghost"
            icon={faClose}
            onClick={() => dispatch(updateDaysSinceReleaseLimit(null))}
          />
        </li>
      )}
      {!!jobDomain && !!jobDomain?.length && (
        <li className="chips__chip">
          {jobDomain.map((jd) => {
            return `${filterOptions?.jobdomeinen.find(s => s.code === jd.id)?.beschrijving}`;
          }).join(', ')}
          <Button 
            className="chips__chip__remove" 
            hover={false}
            layout="ghost"
            icon={faClose}
            onClick={() => dispatch(updateJobDomain([]))}
          />
        </li>
      )}
      {!!job && !!job.length && (
        <li className="chips__chip">
          {job.map((j) => {
            return `${filterOptions?.sjablonen.find(s => s.code === j.id)?.beschrijving}`;
          }).join(', ')}
          <Button 
            className="chips__chip__remove" 
            hover={false}
            layout="ghost"
            icon={faClose}
            onClick={() => dispatch(updateJob([]))}
          />
        </li>
      )}
      {!!workSchedule && (
        <li className="chips__chip">
          {`${filterOptions?.arbeidsregimes.find(s => s.code === workSchedule)?.beschrijving}`}
          <Button 
            className="chips__chip__remove" 
            hover={false}
            layout="ghost"
            icon={faClose}
            onClick={() => dispatch(updateWorkSchedule(null))}
          />
        </li>
      )}
      {!!timeArrangement && !!timeArrangement.length && (
        <li className="chips__chip">
          {timeArrangement.map((ta) => {
            return `${filterOptions?.arbeidsstelsels.find(s => s.code === ta.id)?.beschrijving}`;
          }).join(', ')}
          <Button 
            className="chips__chip__remove" 
            hover={false}
            layout="ghost"
            icon={faClose}
            onClick={() => dispatch(updateTimeArrangement([]))}
          />
        </li>
      )}
      {!!contractType && (
        <li className="chips__chip">
          {`${filterOptions?.contract.find(s => s.code === contractType)?.beschrijving}`}
          <Button 
            className="chips__chip__remove" 
            hover={false}
            layout="ghost"
            icon={faClose}
            onClick={() => dispatch(updateContractType(null))}
          />
        </li>
      )}
      {!!educationLevel && (
        <li className="chips__chip">
          {`${filterOptions?.diplomaniveau.find(s => s.code === educationLevel)?.beschrijving}`}
          <Button 
            className="chips__chip__remove" 
            hover={false}
            layout="ghost"
            icon={faClose}
            onClick={() => dispatch(updateEducationLevel(null))}
          />
        </li>
      )}
      {!!degree && (
        <li className="chips__chip">
          {`${filterOptions?.diplomas.find(s => s.code === degree)?.beschrijving}`}
          <Button 
            className="chips__chip__remove" 
            hover={false}
            layout="ghost"
            icon={faClose}
            onClick={() => dispatch(updateDegree(null))}
          />
        </li>
      )}
      {!!languages && !!languages.length && (
        <li className="chips__chip">
          {languages.map((t) => {
            const level = t?.data?.level;
            return `${filterOptions?.talen.find(s => s.code === t.id)?.beschrijving}`
              + `${level ? ` (${languageLevels.find(ll => ll?.id === t?.data?.level)?.label})` : ''}`;
          }).join(', ')}
          <Button 
            className="chips__chip__remove" 
            hover={false}
            layout="ghost"
            icon={faClose}
            onClick={() => dispatch(updateLanguages([]))}
          />
        </li>
      )}
      {!!driverLicense && !!driverLicense.length && (
        <li className="chips__chip">
          {`Rijbewijs ${driverLicense.map((rb) => {
            return filterOptions?.rijbewijzen?.find(r => r.code === rb.id)?.beschrijving;
          }).join(', ')}`}
          <Button 
            className="chips__chip__remove" 
            hover={false}
            layout="ghost"
            icon={faClose}
            onClick={() => dispatch(updateDriverLicense([]))}
          />
        </li>
      )}
      {Object.entries(checkboxFilters).filter(f => f[1]?.value).map(([id, filter]) => {
        return (
          <li className="chips__chip" key={id}>
            {`${checkboxFilterLabels[id]}`}
            <Button 
              className="chips__chip__remove" 
              hover={false}
              layout="ghost"
              icon={faClose}
              onClick={() => dispatch(filter.update(false))}
            />
          </li>
        );
      })}
    </ul>
  )
}

export default Chips;